import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import './LiveRegion.css';

export interface LiveRegionProps extends Omit<HTMLAttributes<HTMLElement>, 'role' | 'aria-live'> {
  /** Element to render. Use 'span' inside inline/phrasing content. Defaults to 'div'. */
  as?: 'div' | 'span';
  /** Hide the region visually but keep it announced (screen-reader-only text). */
  visuallyHidden?: boolean;
  /** Marks the region as busy (e.g. while loading); sets aria-busy. */
  busy?: boolean;
  children?: ReactNode;
  className?: string;
}

/**
 * LiveRegion — the one polite live region (`role="status"`, `aria-live="polite"`).
 *
 * Use it for passive, non-error announcements: loading text, progress, "saved",
 * result counts. It may also wrap an `<Alert live="off">` when the alert has to
 * appear inside a region that already exists in the DOM.
 * Errors and warnings are never built from this — use `<Alert>`.
 */
export const LiveRegion = forwardRef<HTMLElement, LiveRegionProps>(function LiveRegion(
  { as: Component = 'div', visuallyHidden = false, busy, className = '', children, ...rest },
  ref,
) {
  const classes = ['rg-live-region', visuallyHidden ? 'rg-live-region--visually-hidden' : '', className]
    .filter(Boolean)
    .join(' ');
  return (
    <Component
      ref={ref as never}
      role="status"
      aria-live="polite"
      aria-busy={busy || undefined}
      className={classes}
      {...rest}
    >
      {children}
    </Component>
  );
});

LiveRegion.displayName = 'LiveRegion';
